import type { FetchItemSummariesByCatalogIdQuery } from '@/redux/modules/item/summary/itemSummary.api';

import { fetchAllBidderBiddingInfoIfNeeded } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.actions';
import { fetchBidIncrementsIfNeeded } from '@/redux/modules/bidIncrements/bidIncrements.actions';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit/bidLimit.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { getCatalogSellerId, getCatalogStatus } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchCatalogDetailsIfNeeded } from '@/redux/modules/catalog/detail/catalogDetail.actions';
import { fetchCatalogItemSummaries } from '@/redux/modules/catalog/items/catalogItems.actions';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { fetchConsoleBrandingIfNeeded } from '@/redux/modules/consoleBranding';
import { loadCatalogFavorites } from '@/redux/modules/favorites/favorites.actions';
import { fetchItemDetailsIfNeeded, getItemDetailAgentId } from '@/redux/modules/itemDetail';
import { fetchListingAgentsIfNeeded } from '@/redux/modules/listingAgent';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { fetchSavedItemCountsIfNeeded } from '@/redux/modules/savedItemCount';
import { fetchSellersDetailsIfNeeded } from '@/redux/modules/sellerDetail';
import { fetchSellerFollowerCountIfNeeded } from '@/redux/modules/sellerFollowerCount';
import { fetchSellerRatingSummaryByIdIfNeeded } from '@/redux/modules/sellerRatings';
import { fetchTopSellersIfNeeded } from '@/redux/modules/topRatedSellers';
import { fetchStreamIdIfNeeded } from '@/redux/modules/video';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import TEST_HOUSES from '@/utils/testHouses';
import { getNotFoundUrl } from '@/utils/urls';

const BiddingConsolePageData = async ({ amplitudeExperimentFlags, location, redirect, store }: DataLoaderParams) => {
    const { pathname } = location;
    const { dispatch, getState } = store;

    // If we dont have the catalogId here then we're going to abort
    const catalogId = Number(pathname.split('/').pop()) || 0;
    if (!catalogId) {
        return redirect(getNotFoundUrl());
    }

    // fetch catalog to get sellerId
    await dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));

    // fetch live catalog status
    const sellerId = getCatalogSellerId(getState(), catalogId);
    await dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId, true));

    // If catalog is live, grab data for console
    const catalogStatus = getCatalogStatus(getState(), catalogId);
    if (catalogStatus !== AuctionStatus.Done) {
        const query: FetchItemSummariesByCatalogIdQuery = {
            pageSize: 'all',
            preview: TEST_HOUSES.includes(sellerId),
            sort: SearchSortAndDirection.LotNumberLowest,
        };

        // catalog/console meta/seller data dependencies
        const catalogData = [
            dispatch(fetchBidIncrementsIfNeeded(catalogId)),
            dispatch(fetchCatalogDetailsIfNeeded([catalogId])),
            dispatch(fetchCatalogItemSummaries({ catalogId, query })),
            dispatch(fetchConsoleBrandingIfNeeded(catalogId, sellerId)),
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            ),
            dispatch(fetchSellersDetailsIfNeeded([sellerId])),
            dispatch(fetchStreamIdIfNeeded(catalogId)),
            dispatch(fetchTopSellersIfNeeded()),
            dispatch(loadCatalogFavorites(catalogId)),
        ];

        const hasNewBiddingConsole =
            amplitudeExperimentFlags[AmplitudeExperiments.NewBiddingConsole]?.value ===
            AmplitudeVariants.NewBiddingConsole.Treatment;

        if (hasNewBiddingConsole) {
            catalogData.push(dispatch(fetchSellerFollowerCountIfNeeded([sellerId])));
            catalogData.push(dispatch(fetchSellerRatingSummaryByIdIfNeeded(sellerId)));
        }

        // current item/user data dependencies
        const itemId = getLiveCatalogCurrentItemId(getState(), catalogId);
        const itemData = [
            dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, force: true, itemId })),
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchSavedItemCountsIfNeeded([itemId])),
            dispatch(fetchAllBidderBiddingInfoIfNeeded(false)),
        ];

        // fetch the catalog + current item data
        await Promise.all([...catalogData, ...itemData]);

        // if there is a listing agent, fetch its details
        const agentId = getItemDetailAgentId(getState(), catalogId);
        if (agentId) {
            await dispatch(fetchListingAgentsIfNeeded([agentId]));
        }
    }
};

export default BiddingConsolePageData;
